import { parseApiResponse, extractApiError } from '../lib/safeJson';

export interface ChatSession {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  last_message_at: string;
}

export interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  attachments?: any[];
  metadata?: {
    executionType?: 'fast_followup' | 'full_agent' | 'memory_response';
    processingTimeMs?: number;
    contextHit?: boolean;
    steps?: Array<{ action: string; thought: string }>;
  };
}

// Context-related interfaces for frontend
export interface ContextHint {
  type: 'document' | 'email' | 'search_results' | 'analysis';
  title: string;
  relevance: number;
  lastUsed: string;
}

export interface SendMessageOptions {
  attachments?: any[];
  mcpServices?: Record<string, boolean>;
  preferFastPath?: boolean;
  contextHint?: string;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  statusCode?: number;
  errorData?: any;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        credentials: 'include'
      });
      // Read raw text and safely parse JSON
      const raw = await response.text();
      const parseResult = parseApiResponse(raw);
      const parsed = parseResult.data;
      if (!response.ok) {
        const errorMsg = extractApiError(parsed) || `HTTP error! status: ${response.status}`;
        console.error(`API error for ${endpoint}:`, errorMsg);
        
        // Handle specific token refresh errors
        if (response.status === 401 && parsed?.error === 'TOKEN_REFRESH_FAILED') {
          console.log('Token refresh failed - triggering re-authentication');
          // Dispatch a custom event that the App component can listen to
          window.dispatchEvent(new CustomEvent('TOKEN_REFRESH_FAILED', { 
            detail: { 
              accountsNeedingReauth: parsed.accountsNeedingReauth,
              message: parsed.message 
            }
          }));
        }
        
        return { success: false, error: errorMsg, statusCode: response.status, errorData: parsed };
      }
      return {
        success: true,
        data: parsed as T
      };
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, body: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  async put<T>(endpoint: string, body: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body)
    });
  }

  async patch<T>(endpoint: string, body: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body)
    });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export class ChatService {
  private api: ApiClient;

  constructor() {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    this.api = new ApiClient(`${baseUrl}/api/chat`);
  }

  // Chat session operations
  async getChatSessions(): Promise<ChatSession[]> {
    const response = await this.api.get<{ chatSessions: ChatSession[] }>('/sessions');
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch chat sessions');
    }

    return response.data!.chatSessions;
  }

  async createChatSession(title?: string): Promise<ChatSession> {
    const response = await this.api.post<{ chatSession: ChatSession }>('/sessions', { title });
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to create chat session');
    }

    return response.data!.chatSession;
  }

  async updateChatSessionTitle(sessionId: string, title: string): Promise<ChatSession> {
    const response = await this.api.patch<{ chatSession: ChatSession }>(`/sessions/${sessionId}`, { title });
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to update chat session');
    }

    return response.data!.chatSession;
  }

  async deleteChatSession(sessionId: string): Promise<void> {
    const response = await this.api.delete<{ success: boolean }>(`/sessions/${sessionId}`);
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to delete chat session');
    }
  }

  // Message operations
  async getMessages(sessionId: string): Promise<Message[]> {
    const response = await this.api.get<{ messages: any[] }>(`/sessions/${sessionId}/messages`);
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch messages');
    }

    return response.data!.messages.map(message => ({
      ...message,
      timestamp: typeof message.timestamp === 'string' ? message.timestamp : new Date(message.timestamp).toISOString()
    }));
  }

  // Updated sendMessage method with context support
  async sendMessage(
    sessionId: string, 
    content: string, 
    options: SendMessageOptions = {}
  ): Promise<{
    success: boolean;
    message?: Message;
    error?: string;
    executionType?: 'fast_followup' | 'full_agent' | 'memory_response';
    processingTime?: number;
  }> {
    const { attachments, mcpServices, preferFastPath, contextHint } = options;
    
    const payload: any = {
      message: content,
      mcpServices: mcpServices || {},
      timestamp: new Date().toISOString()
    };

    if (attachments?.length) {
      payload.attachments = attachments;
    }

    if (preferFastPath) {
      payload.preferFastPath = true;
    }

    if (contextHint) {
      payload.contextHint = contextHint;
    }

    try {
      const response = await this.api.post<{ 
        message: any;
        executionType?: string;
        processingTime?: number;
      }>(`/sessions/${sessionId}/messages`, payload);
      
      if (!response.success) {
        return {
          success: false,
          error: response.error || 'Failed to send message'
        };
      }

      const message = response.data!.message;
      return {
        success: true,
        message: {
          ...message,
          timestamp: typeof message.timestamp === 'string' ? message.timestamp : new Date(message.timestamp).toISOString()
        },
        executionType: response.data!.executionType as any,
        processingTime: response.data!.processingTime
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error'
      };
    }
  }

  // Continue search from memory response
  async continueSearch(sessionId: string, continueData: any): Promise<Response> {
    // This returns SSE stream, not a JSON response
    const response = await fetch(`http://localhost:3001/api/chat/sessions/${sessionId}/continue`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(continueData)
    });

    if (!response.ok) {
      throw new Error(`Failed to continue search: ${response.status}`);
    }

    return response; // Return the response for SSE handling
  }

  /**
   * Analyze if a message could benefit from fast follow-up
   */
  static shouldUseFastPath(message: string, conversationHistory: Message[]): {
    useFast: boolean;
    reason: string;
    confidence: number;
  } {
    // Simple heuristics for client-side fast path detection
    const simpleQuestions = [
      /^(am i|is|are|was|were|does|do|did|can|could|would|will)/i,
      /^(what|where|when|why|how|which)/i,
      /\?$/
    ];

    const followupIndicators = [
      /\b(also|and|additionally|furthermore|moreover)\b/i,
      /\b(what about|how about|what if)\b/i,
      /\b(mentioned|said|told|discussed)\b/i,
      /\b(in it|on it|from it|about it)\b/i
    ];

    const requiresNewData = [
      /\b(recent|new|latest|today|yesterday|this week|current)\b/i,
      /\b(find|search|look for|get me)\b/i,
      /\b(all|every|list)\b.*\b(files|documents|emails)\b/i
    ];

    const isSimpleQuestion = simpleQuestions.some(pattern => pattern.test(message));
    const isFollowup = followupIndicators.some(pattern => pattern.test(message));
    const needsNewData = requiresNewData.some(pattern => pattern.test(message));
    const hasRecentContext = conversationHistory.length > 0 && 
      conversationHistory[conversationHistory.length - 1]?.role === 'assistant';

    let confidence = 0.5;
    let useFast = false;
    let reason = 'Neutral assessment';

    if (isSimpleQuestion && hasRecentContext && !needsNewData) {
      confidence = 0.8;
      useFast = true;
      reason = 'Simple question with recent context available';
    } else if (isFollowup && hasRecentContext) {
      confidence = 0.7;
      useFast = true;
      reason = 'Follow-up question detected with context';
    } else if (needsNewData) {
      confidence = 0.9;
      useFast = false;
      reason = 'Requires new data - full search needed';
    } else if (message.length > 200) {
      confidence = 0.6;
      useFast = false;
      reason = 'Complex query - full analysis recommended';
    }

    return { useFast, reason, confidence };
  }
}

export const chatService = new ChatService();
